// 抓取日志 — 查看所有后台抓取任务的执行记录
// 筛选：scraper 名称 / 状态 / 触发类型 / 关键词搜索
// 表格：时间 / scraper / 状态 / 耗时 / 记录数 / 触发方式 / 错误信息
import { useState } from 'react'
import { Card, Select, Input, Tag, Tooltip } from 'antd'
import { Search, CheckCircle2, AlertCircle, Clock, RefreshCw } from 'lucide-react'
import PageHeader from '@/components/PageHeader'
import DataTable from '@/components/DataTable'
import type { ColumnsType } from 'antd/es/table'
import { useCrawlLogs, type CrawlLogItem, type ScraperStat } from '@/mocks/crawlLogs'
import { formatNumber, timeAgo } from '@/lib/utils'

type StatusFilter = 'all' | 'success' | 'failed' | 'partial' | 'running'
type TriggerFilter = 'all' | 'schedule' | 'manual' | 'backfill'

const STATUS_OPTIONS = [
  { label: '全部状态', value: 'all' },
  { label: '成功', value: 'success' },
  { label: '失败', value: 'failed' },
  { label: '部分成功', value: 'partial' },
  { label: '运行中', value: 'running' },
]

const TRIGGER_OPTIONS = [
  { label: '全部来源', value: 'all' },
  { label: '定时调度', value: 'schedule' },
  { label: '手动触发', value: 'manual' },
  { label: '历史回填', value: 'backfill' },
]

const STATUS_MAP: Record<string, { color: string; icon: React.ReactNode; text: string }> = {
  success: { color: 'success', icon: <CheckCircle2 size={12} />, text: '成功' },
  failed: { color: 'error', icon: <AlertCircle size={12} />, text: '失败' },
  expired: { color: 'error', icon: <AlertCircle size={12} />, text: '过期' },
  partial: { color: 'warning', icon: <Clock size={12} />, text: '部分成功' },
  running: { color: 'processing', icon: <RefreshCw size={12} className="animate-spin" />, text: '运行中' },
}

const TRIGGER_MAP: Record<string, string> = {
  schedule: '定时',
  manual: '手动',
  backfill: '回填',
  webhook: 'Webhook',
}

function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null) return '—'
  if (seconds < 60) return `${seconds.toFixed(1)}s`
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return `${m}m${s}s`
}

function formatTimestamp(ms: number): string {
  const d = new Date(ms)
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${d.getMonth() + 1}/${d.getDate()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

export default function CrawlLogs() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [triggerFilter, setTriggerFilter] = useState<TriggerFilter>('all')
  const [scraperFilter, setScraperFilter] = useState<string | undefined>(undefined)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)

  const { data, isLoading } = useCrawlLogs({
    days: 30,
    page,
    page_size: pageSize,
    status: statusFilter === 'all' ? undefined : statusFilter,
    trigger_type: triggerFilter === 'all' ? undefined : triggerFilter,
    scraper_name: scraperFilter,
    search: search || undefined,
  })

  const stats = data?.stats
  const items = data?.items ?? []
  const total = data?.total ?? 0

  // 从 stats 中提取所有 scraper 名称用于筛选下拉
  const scraperOptions = (stats?.byScraper ?? []).map((s: ScraperStat) => ({
    label: s.name,
    value: s.name,
  }))

  const columns: ColumnsType<CrawlLogItem> = [
    {
      title: '时间',
      dataIndex: 'startedAt',
      key: 'startedAt',
      width: 140,
      render: (v: number) => (
        <span className="text-ink-700 text-[13px] font-mono">{formatTimestamp(v)}</span>
      ),
    },
    {
      title: 'Scraper',
      dataIndex: 'scraperName',
      key: 'scraperName',
      width: 200,
      render: (v: string) => (
        <span className="text-ink-900 text-[13px] font-medium">{v}</span>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (v: string) => {
        const cfg = STATUS_MAP[v] || { color: 'default', icon: null, text: v }
        return (
          <Tag color={cfg.color} icon={cfg.icon} className="!mr-0">
            {cfg.text}
          </Tag>
        )
      },
    },
    {
      title: '耗时',
      dataIndex: 'durationSeconds',
      key: 'durationSeconds',
      width: 80,
      render: (v: number | null) => (
        <span className="num text-ink-700 text-[13px]">{formatDuration(v)}</span>
      ),
    },
    {
      title: '记录数',
      dataIndex: 'recordsCount',
      key: 'recordsCount',
      width: 80,
      render: (v: number | null) => (
        <span className="num text-ink-700 text-[13px]">{v != null ? formatNumber(v) : '—'}</span>
      ),
    },
    {
      title: '触发',
      dataIndex: 'triggerType',
      key: 'triggerType',
      width: 70,
      render: (v: string) => (
        <span className="text-ink-500 text-[12px]">{TRIGGER_MAP[v] || v}</span>
      ),
    },
    {
      title: '错误信息',
      dataIndex: 'errorMsg',
      key: 'errorMsg',
      ellipsis: true,
      render: (v: string | null) =>
        v ? (
          <Tooltip title={v} placement="topLeft">
            <span className="text-red-500 text-[12px] truncate block max-w-[280px]">{v}</span>
          </Tooltip>
        ) : (
          <span className="text-ink-300 text-[12px]">—</span>
        ),
    },
  ]

  return (
    <div>
      <PageHeader
        title="抓取日志"
        subtitle="所有后台抓取任务的执行记录与统计"
        extra={
          <span className="text-sm text-ink-500 num">
            共 {formatNumber(total)} 条记录
          </span>
        }
      />

      {/* 摘要卡片 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
        <Card size="small" className="!border-ink-100">
          <div className="text-xs text-ink-500 mb-1">总执行次数</div>
          <div className="text-xl font-semibold text-ink-900 num">
            {stats ? formatNumber(stats.totalRuns) : '—'}
          </div>
        </Card>
        <Card size="small" className="!border-ink-100">
          <div className="text-xs text-ink-500 mb-1">成功率</div>
          <div className="text-xl font-semibold num" style={{ color: (stats?.successRate ?? 0) >= 90 ? '#07C160' : '#FA5151' }}>
            {stats ? `${stats.successRate}%` : '—'}
          </div>
        </Card>
        <Card size="small" className="!border-ink-100">
          <div className="text-xs text-ink-500 mb-1">失败次数</div>
          <div className="text-xl font-semibold num" style={{ color: (stats?.failedRuns ?? 0) > 0 ? '#FA5151' : '#07C160' }}>
            {stats ? formatNumber(stats.failedRuns) : '—'}
          </div>
        </Card>
        <Card size="small" className="!border-ink-100">
          <div className="text-xs text-ink-500 mb-1">总抓取记录</div>
          <div className="text-xl font-semibold text-ink-900 num">
            {stats ? formatNumber(stats.totalRecords) : '—'}
          </div>
        </Card>
      </div>

      {/* 各 scraper 执行情况 */}
      {stats && stats.byScraper.length > 0 && (
        <Card title="各 Scraper 执行情况" size="small" className="mb-4 !border-ink-100">
          <div className="flex flex-wrap gap-2">
            {stats.byScraper.map((s: ScraperStat) => (
              <div
                key={s.name}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-ink-50 text-[12px] cursor-pointer hover:bg-ink-100 transition-colors"
                onClick={() => setScraperFilter(scraperFilter === s.name ? undefined : s.name)}
              >
                <span className="text-ink-700 font-medium">{s.name}</span>
                <span className="text-ink-500 num">{s.runs}次</span>
                {s.failed > 0 && (
                  <span className="text-red-500 num">{s.failed}次失败</span>
                )}
                <span className="text-ink-400 num">{formatNumber(s.rows)}条</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* 筛选栏 */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Select
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); setPage(1) }}
          options={STATUS_OPTIONS}
          className="!w-32"
          size="small"
        />
        <Select
          value={triggerFilter}
          onChange={(v) => { setTriggerFilter(v); setPage(1) }}
          options={TRIGGER_OPTIONS}
          className="!w-32"
          size="small"
        />
        <Select
          value={scraperFilter}
          onChange={(v) => { setScraperFilter(v); setPage(1) }}
          options={[{ label: '全部 Scraper', value: undefined }, ...scraperOptions]}
          allowClear
          placeholder="全部 Scraper"
          className="!w-48"
          size="small"
        />
        <Input
          allowClear
          prefix={<Search size={14} className="text-ink-300" />}
          placeholder="搜索 scraper 名称 / 错误信息"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1) }}
          className="!w-64"
          size="small"
        />
      </div>

      {/* 表格 */}
      <DataTable
        columns={columns}
        dataSource={items}
        loading={isLoading}
        rowKey="id"
        size="small"
        scroll={{ x: 760 }}
        pagination={{
          current: page,
          pageSize,
          total,
          pageSizeOptions: [20, 50, 100],
          showSizeChanger: true,
          showTotal: (t: number) => `共 ${t} 条`,
          onChange: (p: number, ps: number) => { setPage(p); setPageSize(ps) },
        }}
      />
    </div>
  )
}
